import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable } from 'rxjs';
import { environment } from '../../../../../environments/environment';
import { Scenario, ReferenceData } from '../viewmodels/executions-viewmodels';
import { AuthService } from '../../../../auth/login/services/auth.service';

@Injectable({
  providedIn: 'root'
})
export class ExecutionService {
  private apiUrl = `${environment.apiUrl}`;

  constructor(private http: HttpClient, private authService: AuthService) {}

  getAllScenarios(): Observable<Scenario[]> {
    return this.http.get<Scenario[]>(`${this.apiUrl}/scenarios`);
  }


  getScenariosByClient(clientId: string): Observable<Scenario[]> {
    if (clientId === 'default') {
      return this.http.get<Scenario[]>(`${this.apiUrl}/scenarios/templates`);
    }
    return this.http.get<Scenario[]>(`${this.apiUrl}/scenarios/by-client/${clientId}`);
  }

  getScenariosFiltered(filterPayload: any, page: number = 1, pageSize: number = 10, sortBy: string = '', sortOrder: string = ''): Observable<{ data: Scenario[]; total: number; page: number; pageSize: number }> {
    return this.http.post<{ data: Scenario[]; total: number; page: number; pageSize: number }>(
      `${this.apiUrl}/scenarios/filter`,
      { ...filterPayload, page, pageSize, sortBy, sortOrder }
    );
  }

  getGroupingSummary(filterPayload: any): Observable<{ id: string; scenarioName: string; functionalAreaName: string; testTypeName: string }[]> {
    return this.http.post<{ id: string; scenarioName: string; functionalAreaName: string; testTypeName: string }[]>(
      `${this.apiUrl}/scenarios/grouping-summary`,
      filterPayload
    );
  }


 
  getScenarioById(id: string): Observable<Scenario> {
    return this.http.get<Scenario>(`${this.apiUrl}/scenarios/${id}`);
  }

  deleteScenario(id: string): Observable<void> {
    return this.http.delete<void>(`${this.apiUrl}/scenarios/${id}`);
  }

  getReferenceList(codeKeys: string[]): Observable<any[]> {
    return this.http.post<any[]>(`${environment.apiUrl}/reference-values/by-code-keys`, { codeKeys });
  }

  executeScenarios(scenarioIds: string[]): Observable<any> {
    return this.http.post<any>(`${this.apiUrl}/execute`, { scenarioIds });
  }

  runScenario(scenarioId: string): Observable<any> {
    return this.http.post<any>(`${this.apiUrl}/run/${scenarioId}`, {});
  }

  /**
   * Enqueue scenario(s) to run on cloud (ECS runner via SQS)
   * Backend accepts: POST /runner/enqueue { scenarioId } or { scenarioIds: [...] }
   */
  enqueueCloudScenarioRun(scenarioIds: string | string[], tenantId?: string): Observable<any> {
    const payload: any = Array.isArray(scenarioIds)
      ? { scenarioIds }
      : { scenarioId: scenarioIds };
    if (tenantId) {
      payload.tenantId = tenantId;
    }
    return this.http.post<any>(`${this.apiUrl}/runner/enqueue`, payload);
  }

  updateJobStatus(jobId: string, status: string): Observable<any> {
    return this.http.put<any>(`${this.apiUrl}/runner/jobs/${jobId}/status`, { status });
  }

  /**
   * Fetch cloud jobs by userId
   */
  getMyCloudJobsByUserId(userId: string): Observable<any> {
    return this.http.get<any>(`${this.apiUrl}/runner/jobs/${userId}`);
  }

  /**
   * Fetch a single job by jobId
   */
  getJobById(jobId: string): Observable<any> {
    return this.http.get<any>(`${this.apiUrl}/runner/job/${jobId}`);
  }

  getAllPackages(): Observable<any[]> {
    return this.http.get<any[]>(`${environment.apiUrl}/packages`);
  }

  
  getUserPackages(): Observable<any[]> {
    return this.http.get<any[]>(`${environment.apiUrl}/packages/user-packages`);
  }

  getClientPackages(clientId: string): Observable<any[]> {
    return this.http.get<any[]>(`${environment.apiUrl}/client-package-mapping/client/${clientId}`);
  }

  getClientTenants(clientId: string): Observable<any[]> {
    return this.http.get<any[]>(`${environment.apiUrl}/client-tenants/client/${clientId}`);
  }

  getAllFunctionalAreas(): Observable<any[]> {
    return this.http.get<any[]>(`${environment.apiUrl}/functional-areas`);
  }

  getUserFunctionalAreas(): Observable<any[]> {
    return this.http.get<any[]>(`${environment.apiUrl}/functional-areas/user-functional-areas`);
  }

  getAllClients(): Observable<any[]> {
    return this.http.get<any[]>(`${environment.apiUrl}/clients/user-clients`);
  }

  getIncompleteAuthProfileIds(): Observable<string[]> {
    return this.http.get<string[]>(`${this.apiUrl}/auth-profiles/incomplete-profiles`);
  }
}
